import { BaseDataProvider } from "./baseProvider";

export class ReferralDataProvider extends BaseDataProvider {
  constructor() {
    super("referrals");
  }

  async getList(_resource: string, params: Record<string, any>) {
    const { page, perPage } = params.pagination || { page: 1, perPage: 20 };
    const filter = params.filter || {};

    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: perPage.toString(),
    });
    if (filter.status) queryParams.append("status", filter.status);

    const endpoint = `referrals/codes?${queryParams.toString()}`;
    const response = (await this.makeRequest(endpoint)) as {
      data: unknown[];
      total: number;
    };

    return { data: response.data || [], total: response.total || 0 };
  }

  async getOne(_resource: string, params: Record<string, unknown>) {
    const response = await this.makeRequest(`referrals/codes/${params.id}`);
    return { data: response };
  }

  async getMany(resource: string, params: Record<string, unknown>) {
    const promises = (params.ids as string[]).map((id) =>
      this.getOne(resource, { id }),
    );
    const results = await Promise.all(promises);
    return { data: results.map((r) => r.data) };
  }

  async getManyReference() {
    return { data: [], total: 0 };
  }

  async create(_resource: string, params: Record<string, unknown>) {
    const response = await this.makeRequest("referrals/codes", {
      method: "POST",
      body: JSON.stringify(params.data),
      headers: new Headers({ "Content-Type": "application/json" }),
    });
    return { data: response };
  }

  async update(_resource: string, params: Record<string, unknown>) {
    // Only label/notes/status/maxRedemptions are editable — the backend
    // whitelists and drops everything else, but scope what we send anyway
    // so the Edit form can't imply the markup/expiry terms are changeable.
    const data = params.data as Record<string, unknown>;
    const body = {
      label: data.label,
      notes: data.notes,
      status: data.status,
      maxRedemptions: data.maxRedemptions,
    };
    const response = await this.makeRequest(`referrals/codes/${params.id}`, {
      method: "PUT",
      body: JSON.stringify(body),
      headers: new Headers({ "Content-Type": "application/json" }),
    });
    return { data: response };
  }

  async updateMany(_resource: string, params: Record<string, unknown>) {
    const promises = (params.ids as string[]).map((id) =>
      this.update(_resource, { id, data: params.data }),
    );
    const results = await Promise.all(promises);
    return { data: results.map((r) => r.data) };
  }

  async delete(_resource: string, params: Record<string, unknown>) {
    await this.makeRequest(`referrals/codes/${params.id}`, { method: "DELETE" });
    return { data: { id: params.id } };
  }

  async deleteMany(_resource: string, params: Record<string, unknown>) {
    const promises = (params.ids as string[]).map((id) =>
      this.delete(_resource, { id }),
    );
    await Promise.all(promises);
    return { data: params.ids };
  }

  async getRedemptions(codeId: string, page = 1, perPage = 20) {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: perPage.toString(),
    });
    return this.makeRequest(`referrals/codes/${codeId}/redemptions?${queryParams.toString()}`);
  }
}
